import PropTypes from "prop-types";
import { Box, Card, Chip, Grid, Typography } from "@mui/material";

// Statuts d'une inscription active (apprenant inscrit à la session)
const ACTIVE_STATUSES = ["Inscrit", "InscritD"];
// Statuts considérés comme annulés (revenu perdu)
const CANCELLED_STATUSES = ["Annulation", "À décaler", "Décalé"];

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const formatEur = (value) =>
  `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Math.round(value)
  )} €`;

// Répartit une formation dans l'un des buckets : annulée / terminée / en cours.
// - terminée : inscrit/inscritD avec progression à 100%
// - en cours : inscrit/inscritD avec progression entre 0 et 100 (100 exclu)
// - annulée  : annulation / à décaler / décalé
// Les autres statuts (en attente, à transférer…) ne sont pas comptabilisés.
const classify = (formation) => {
  if (CANCELLED_STATUSES.includes(formation.status)) {
    return "cancelled";
  }
  if (ACTIVE_STATUSES.includes(formation.status)) {
    const progression = toNumber(
      formation.totalProgress ?? formation.progression
    );
    return progression >= 100 ? "done" : "ongoing";
  }
  return "other";
};

const plural = (count, singular) =>
  `${count} ${singular}${count > 1 ? "s" : ""}`;

const IndicatorTile = ({ value, valueColor, label, sub, bgcolor }) => (
  <Box
    sx={{
      p: 2,
      height: "100%",
      borderRadius: "12px",
      backgroundColor: bgcolor,
    }}
  >
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: "1.5rem",
        lineHeight: 1.1,
        color: valueColor,
        letterSpacing: "-0.5px",
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        mt: 1,
        fontWeight: 700,
        fontSize: "0.72rem",
        letterSpacing: "0.6px",
        textTransform: "uppercase",
        color: valueColor,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ mt: 0.5, fontSize: "0.8rem", fontWeight: 600, color: valueColor, opacity: 0.85 }}
    >
      {sub}
    </Typography>
  </Box>
);

IndicatorTile.propTypes = {
  value: PropTypes.string,
  valueColor: PropTypes.string,
  label: PropTypes.string,
  sub: PropTypes.string,
  bgcolor: PropTypes.string,
};

const CustomerIndicators = ({ formations = [] }) => {
  const stats = formations.reduce(
    (acc, formation) => {
      const price = toNumber(formation.real_price);
      const bucket = classify(formation);
      if (bucket === "cancelled") {
        acc.cancelledAmount += price;
        acc.cancelledCount += 1;
      } else if (bucket === "done") {
        acc.doneAmount += price;
        acc.doneCount += 1;
      } else if (bucket === "ongoing") {
        acc.ongoingAmount += price;
        acc.ongoingCount += 1;
      }
      return acc;
    },
    {
      doneAmount: 0,
      doneCount: 0,
      ongoingAmount: 0,
      ongoingCount: 0,
      cancelledAmount: 0,
      cancelledCount: 0,
    }
  );

  // CA total = somme des trois indicateurs (terminé + en cours + pertes)
  const totalAmount =
    stats.doneAmount + stats.ongoingAmount + stats.cancelledAmount;

  const headerBadge = (
    <Chip
      label={`${plural(formations.length, "formation")} · CA total ${formatEur(
        totalAmount
      )}`}
      sx={{
        fontWeight: 600,
        fontSize: "0.78rem",
        color: "#475569",
        bgcolor: "#f1f5f9",
        borderRadius: "10px",
      }}
    />
  );

  return (
    <Card
      sx={{
        p: 3,
        mb: 3,
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 4px 20px -2px rgba(0,0,0,0.04)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: formations.length === 0 ? 1.5 : 2.5,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#1e293b" }}>
          Indicateurs client
        </Typography>
        {headerBadge}
      </Box>

      {formations.length === 0 ? (
        <Typography sx={{ color: "#94a3b8", fontWeight: 500 }}>
          Aucune inscription pour cet apprenant.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <IndicatorTile
              value={formatEur(stats.doneAmount)}
              valueColor="#059669"
              label="CA terminé"
              sub={plural(stats.doneCount, "formation")}
              bgcolor="rgba(16, 185, 129, 0.08)"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <IndicatorTile
              value={formatEur(stats.ongoingAmount)}
              valueColor="#2563eb"
              label="CA en cours"
              sub={plural(stats.ongoingCount, "inscription")}
              bgcolor="rgba(59, 130, 246, 0.08)"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <IndicatorTile
              value={
                stats.cancelledAmount > 0
                  ? `−${formatEur(stats.cancelledAmount)}`
                  : formatEur(0)
              }
              valueColor="#dc2626"
              label="Pertes"
              sub={plural(stats.cancelledCount, "annulée")}
              bgcolor="rgba(239, 68, 68, 0.06)"
            />
          </Grid>
        </Grid>
      )}
    </Card>
  );
};

CustomerIndicators.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  formations: PropTypes.array,
};

export default CustomerIndicators;
